import { Trans, useTranslation } from 'next-i18next';
import { GiftIcon } from '@heroicons/react/24/outline';
import { CursorArrowRaysIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';

import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';

import { useReferralCredits } from './use-referral-credits';

/**
 * Hero section with the main heading, description, hero image,
 * floating "Gift/Get" cards, and a CTA to share the referral link.
 */
export function ReferralHeroSection() {
  const { t } = useTranslation('referral-page');
  const { inviter, invitee } = useReferralCredits();

  return (
    <SectionContainer>
      {/* Main heading */}
      <Heading>
        <Trans
          i18nKey="hero_heading"
          ns="referral-page"
          values={{ inviter, invitee }}
          components={{
            purple: <HeadingPurple />,
            dark: <HeadingDark />,
            br: <br />,
          }}
        />
      </Heading>

      {/* Description */}
      <Description>{t('hero_description', { invitee })}</Description>

      {/* Hero image with floating cards */}
      <HeroImageContainer>
        <Image
          className="w-full rounded-lg object-cover aspect-[1536/640] object-[center_35%]"
          src={getAssetSrc('/images/referral/referral-hero-brigade.jpg')}
          alt="Brigata di ristorazione che brinda insieme su una terrazza"
          width={1456}
          height={832}
          loader={customImageLoader}
        />

        {/* Floating "Gift" card - bottom left, overlapping image */}
        <GiftCard>
          <CardIconWrapper className="bg-[#F0EBF8]">
            <CursorArrowRaysIcon className="w-6 h-6 text-[#6633BB]" />
          </CardIconWrapper>
          <CardTextWrapper>
            <CardLabel>{t('gift_label')}</CardLabel>
            <CardValue>{t('plus_free_days', { days: invitee })}</CardValue>
          </CardTextWrapper>
        </GiftCard>

        {/* Floating "Get" card - bottom right, overlapping image */}
        <GetCard>
          <CardIconWrapper className="bg-[#6633BB]">
            <GiftIcon className="w-6 h-6 text-[#F0EBF8]" />
          </CardIconWrapper>
          <CardTextWrapper>
            <CardLabel>{t('get_label')}</CardLabel>
            <CardValue>{t('plus_free_days', { days: inviter })}</CardValue>
          </CardTextWrapper>
        </GetCard>
      </HeroImageContainer>

      {/* Keyframe animation styles */}
      <style jsx global>{`
        @keyframes floatGiftCard {
          0%,
          100% {
            transform: translateY(0) rotate(-17.36deg);
          }
          50% {
            transform: translateY(-12px) rotate(-23deg);
          }
        }
        @keyframes floatGetCard {
          0%,
          100% {
            transform: translateY(0) rotate(17.39deg);
          }
          50% {
            transform: translateY(12px) rotate(11.5deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .floating-gift-card,
          .floating-get-card {
            animation: none !important;
          }
        }
      `}</style>
    </SectionContainer>
  );
}

export default ReferralHeroSection;

// Layout Components
const SectionContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center gap-6 w-full', className)} {...props} />
);

const Heading = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h1
    className={cn('text-center whitespace-pre-line font-praktikal-bold text-[20px] lg:text-[30px]', className)}
    style={{ fontWeight: 800, lineHeight: '1.275em' }}
    {...props}
  />
);

const HeadingPurple = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-[#6633BB]', className)} {...props} />
);

const HeadingDark = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-[#1C1C33]', className)} {...props} />
);

const Description = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p
    className={cn('text-base font-normal text-[#1C1C33] text-center whitespace-pre-line leading-[1.21]', className)}
    {...props}
  />
);

const HeroImageContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative w-full', className)} {...props} />
);

const FloatingCard = ({ className, style, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('absolute flex items-center gap-2 bg-white border border-[#DEDEE1] rounded-lg p-3', className)}
    style={{ boxShadow: '0px 2px 16px 0px rgba(0, 0, 26, 0.25)', ...style }}
    {...props}
  />
);

const GiftCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <FloatingCard
    className={cn('floating-gift-card -left-4 sm:left-2 lg:-left-20', className)}
    style={{
      top: '20%',
      transform: 'rotate(-17.36deg)',
      animation: 'floatGiftCard 8s ease-in-out infinite',
      boxShadow: '0px 2px 16px 0px rgba(0, 0, 26, 0.25)',
    }}
    {...props}
  />
);

const GetCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <FloatingCard
    className={cn('floating-get-card -right-4 sm:right-2 lg:-right-20', className)}
    style={{
      backgroundColor: '#f4fac0',
      top: '55%',
      transform: 'rotate(17.39deg)',
      animation: 'floatGetCard 8s ease-in-out infinite',
      animationDelay: '0.6s',
      boxShadow: '0px 2px 16px 0px rgba(0, 0, 26, 0.25)',
    }}
    {...props}
  />
);

const CardIconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-center p-2 rounded', className)} {...props} />
);

const CardTextWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col justify-center', className)} {...props} />
);

const CardLabel = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-lg font-normal text-[#1C1C33] leading-[1.21]', className)} {...props} />
);

const CardValue = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-lg text-[#6633BB] leading-[1.21]', className)} style={{ fontWeight: 900 }} {...props} />
);
